import React, { useEffect, useRef, useState } from 'react';
import { Layers, Search, Star, ChevronDown, Loader2 } from 'lucide-react';

// Priority when the property has no Preferred Quote Selection (Maintenance last).
const GROUP_ORDER = ['Repairs', 'Re-Coat', 'Re-Roof', 'Restoration', 'Remedial Repairs', 'Guttering', 'Maintenance'];

const groupOf = (qt) => {
  if ((qt.ref || '').toLowerCase().startsWith('remedial')) return 'Remedial Repairs';
  const c = (qt.quote_categories || [])[0];
  return c || 'Other';
};
const rank = (g) => { const i = GROUP_ORDER.indexOf(g); return i === -1 ? GROUP_ORDER.length + 1 : i; };
const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

function QtRow({ qt, onPick, testId }) {
  const rts = (qt.roof_types || []).filter(r => r !== 'All');
  return (
    <button type="button" role="menuitem" onClick={() => onPick(qt)} data-testid={testId}
      className="w-full px-3 py-1.5 text-left text-[12px] text-slate-800 hover:bg-emerald-50 transition-colors flex items-center justify-between gap-2">
      <span className="font-medium truncate">{qt.ref}</span>
      {rts.length > 0 && <span className="text-[9px] text-slate-400 flex-shrink-0">{rts.slice(0, 2).join('/')}</span>}
    </button>
  );
}

// Property page · Button 2 — pick a Quote Type and create the appraisal with it.
export function QuoteTypeQuickPick({ api, roofTypes = [], preferred = [], onPick, compact = false, testIdPrefix = 'property-qt-pick' }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState([]);
  const [roofMatched, setRoofMatched] = useState(null);
  const [filter, setFilter] = useState('');
  const wrap = useRef(null);
  const roofKey = (roofTypes || []).join('|');

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open || roofMatched) return;
    (async () => {
      const allRes = await api.get('/quote-types-catalog').catch(() => ({ data: { items: [] } }));
      setAll(allRes.data?.items || []);
      const roofs = roofKey ? roofKey.split('|') : [];
      if (!roofs.length) { setRoofMatched(allRes.data?.items || []); return; }
      const res = await Promise.all(roofs.map(r => api.get('/quote-types-catalog', { params: { roof_type: r } }).catch(() => ({ data: { items: [] } }))));
      const seen = new Map();
      res.forEach(r => (r.data?.fallback ? [] : (r.data?.items || [])).forEach(q => seen.set(q.ref, q)));
      setRoofMatched(Array.from(seen.values()));
    })();
  }, [open, roofMatched, api, roofKey]);

  const pick = (qt) => { setOpen(false); setFilter(''); onPick(qt); };
  const pref = (preferred || []).filter(Boolean);
  const shortlist = (roofMatched || [])
    .filter(q => GROUP_ORDER.includes(groupOf(q)))
    .filter(q => !pref.length || pref.includes(groupOf(q)))
    .sort((a, b) => rank(groupOf(a)) - rank(groupOf(b)) || a.ref.localeCompare(b.ref));
  const groups = [];
  shortlist.forEach(q => {
    const g = groupOf(q);
    const last = groups[groups.length - 1];
    if (last && last.g === g) last.items.push(q); else groups.push({ g, items: [q] });
  });
  const norm = (t) => (t || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const searched = all
    .filter(q => !filter || norm(q.ref).includes(norm(filter)))
    .sort((a, b) => rank(groupOf(a)) - rank(groupOf(b)) || a.ref.localeCompare(b.ref));
  const size = compact ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm';

  return (
    <div ref={wrap} className="relative inline-flex">
      <button type="button" onClick={() => setOpen(v => !v)} data-testid={`${testIdPrefix}-btn`}
        className={`flex items-center gap-1.5 ${size} bg-white text-emerald-800 border border-emerald-600 rounded-md font-semibold hover:bg-emerald-50 transition-colors shadow-sm`}>
        <Layers size={compact ? 12 : 14} /> Quote Type <ChevronDown size={compact ? 12 : 14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" data-testid={`${testIdPrefix}-menu`}
          className={`absolute ${compact ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} right-0 w-[22rem] bg-white border border-[#E2E2DF] rounded-lg shadow-xl z-50 flex flex-col max-h-[55vh]`}>
          <div className="px-3 pt-3 pb-1 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 flex items-center gap-1.5">
              <Star size={11} /> {pref.length ? `Preferred: ${pref.join(', ')}` : 'Recommended for this roof'}
            </p>
            <span className="text-[10px] text-slate-400 truncate max-w-[130px]">{(roofTypes || []).join(', ')}</span>
          </div>
          <div className="overflow-y-auto max-h-[24vh] border-b border-slate-100" data-testid={`${testIdPrefix}-shortlist`}>
            {!roofMatched ? (
              <p className="px-3 py-3 text-[11px] text-slate-400 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Loading…</p>
            ) : groups.length === 0 ? (
              <p className="px-3 py-2 text-[11px] text-slate-400 italic">No matches — search all quotation types below.</p>
            ) : groups.map(({ g, items }) => (
              <div key={g}>
                <p className="px-3 pt-1.5 pb-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-slate-400 bg-slate-50/60">{g}</p>
                {items.map(q => <QtRow key={q.ref} qt={q} onPick={pick} testId={`${testIdPrefix}-reco-${slug(q.ref)}`} />)}
              </div>
            ))}
          </div>
          <div className="px-3 pt-2 pb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 flex items-center justify-between">
              <span>All quotation types</span><span className="text-slate-400 font-normal">{searched.length} avail.</span>
            </p>
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search e.g. Reroof, Treatment…"
                data-testid={`${testIdPrefix}-search`}
                className="w-full pl-7 pr-2 py-1.5 text-[11px] border border-[#E2E2DF] rounded focus:outline-none focus:border-emerald-500" />
            </div>
          </div>
          <div className="overflow-y-auto min-h-[100px] max-h-[20vh] pb-2" data-testid={`${testIdPrefix}-all`}>
            {searched.map(q => <QtRow key={`all-${q.ref}`} qt={q} onPick={pick} testId={`${testIdPrefix}-qt-${slug(q.ref)}`} />)}
          </div>
        </div>
      )}
    </div>
  );
}
